import CheckoutBasketItem from "../../Checkout/CheckoutBasketItem.js";
import DataTypes from "./OrderQuestionDataTypes.js";

/**
 * Order question service
 *
 * Utility methods to handle interactions with checkout order questions.
 *
 * @package HOPS
 * @subpackage Orders
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class OrderQuestionService {

	/**
	 * Get the number of valid question answers in an answers object
	 * for a set of questions and a basket's item claims.
	 *
	 * The answers object should match the format produced by 
	 * the `OrderQuestionSet` component in `@hps/hops-react`.
	 *
	 * This currently assumes all questions are mandatory!
	 *
	 * @param {Object} questionsData E.g. `/api/checkout/basket/{Basket}/questions`
	 * @param {Object} answersData
	 * @param {Array<Object>} itemClaims `CheckoutBasketItem` objects in the basket
	 * @return {Integer}
	 */
	static getAnswersCount(questionsData, answersData, itemClaims) {

		let count = 0;

		/** Get the number of per-item/per-person questions answered */
		for (const itemClaim of itemClaims) {
			count += this.getItemAnswersCount(questionsData, answersData, itemClaim);
		}

		/** Get the number of per-order questions answered */
		for (const orderQuestion of (questionsData?.Order || [])) {
			const question = questionsData?.Questions?.find(q => (q.Id === orderQuestion.Question));
			if (this.validateAnswer(answersData?.Order?.[question?.Id], question?.DataType)) {
				count++;
			}
		}

		return count;

	}


	/**
	 * Get the total number of questions that the customer needs to 
	 * be asked given a questions data object from the API and the set of 
	 * item claims in the customer's basket.
	 *
	 * @param {Object} questionsData E.g. `/api/checkout/basket/{Basket}/questions`
	 * @param {Array<Object>} itemClaims `CheckoutBasketItem` objects in the basket
	 * @return {Integer}
	 */
	static getQuestionsCount(questionsData, itemClaims) {

		let questionsCount = 0;

		/**
		 * Iterate of the item claims
		 */
		for (const itemClaim of itemClaims) {

			/**
			 * Account for this item's per-item/per-person questions
			 */
			questionsCount += this.getItemQuestionsCount(
				questionsData,
				itemClaim
			);

		}

		/**
		 * Now add the count of the per-order questions
		 */
		const questionsDataOrder = questionsData?.Order;
		questionsCount += questionsDataOrder ? questionsDataOrder.length : 0;

		return questionsCount;

	}


	/**
	 * Get the number of valid question answers in an answers 
	 * object for a certain questions object that pertain to a 
	 * specific item claim object.
	 * 
	 * The answers object should match the format produced by 
	 * the `OrderQuestionSet` component in `@hps/hops-react`.
	 *
	 * This currently assumes all questions are mandatory!
	 * 
	 * @param {Array<Object>} itemClaims `CheckoutBasketItem` objects in the basket
	 * @param {Object} answersData
	 * @param {Object} itemClaim
	 * @return {Integer}
	 */
	static getItemAnswersCount(questionsData, answersData, itemClaim) {

		let count = 0;
		const perItemQuestions = (questionsData?.Items?.[itemClaim.Uuid] || []);
		const perPersonQuestions = (questionsData?.People?.[itemClaim.Uuid] || []);

		/** Iterate and count per-item question answers */
		for (const itemQuestion of perItemQuestions) {

			const unitCount = itemClaim.Quantity;
			const question = questionsData?.Questions?.find(q => (q.Id === itemQuestion.Question));

			for (let unit = 0; unit < unitCount; unit++) {
				const unitAnswers = answersData?.Items?.[itemClaim.Uuid]?.[unit];
				if (this.validateAnswer(unitAnswers?.[question?.Id], question?.DataType)) {
					count++;
				}
			}

		}

		/** Iterate and count per-person question answers */
		for (const personQuestion of perPersonQuestions) {

			const unitCount = itemClaim.Quantity;
			const peopleCount = CheckoutBasketItem.getTargetPeopleCount(itemClaim);
			const question = questionsData?.Questions?.find(q => (q.Id === personQuestion.Question));

			for (let person = 0; person < peopleCount; person++) {
				for (let unit = 0; unit < unitCount; unit++) {
					const unitAnswers = answersData?.People?.[person]?.[itemClaim.Uuid]?.[unit];
					if (this.validateAnswer(unitAnswers?.[question?.Id], question?.DataType)) {
						count++;
					}
				}
			}

		}

		return count;

	}


	/**
	 * Get the total number of questions that the customer needs to 
	 * be asked for a particular item claim, given a questions data object.
	 *
	 * @param {Object} questionsData E.g. `/api/checkout/basket/{Basket}/questions`
	 * @param {Object} itemClaim `CheckoutBasketItem` object in the basket
	 * @return {Integer}
	 */
	static getItemQuestionsCount(questionsData, itemClaim) {

		/** Per-item questions (asked once per item) */
		const perItemQuestions = (questionsData?.Items[itemClaim.Uuid]?.length || 0);

		/** Per-person questions (asked once per person per item) */
		const perPersonQuestions = (questionsData?.People[itemClaim.Uuid]?.length || 0);

		/** Number of people covered by this item */
		const peopleCount = CheckoutBasketItem.getTargetPeopleCount(itemClaim);

		return ((perItemQuestions + (perPersonQuestions * peopleCount)) * itemClaim.Quantity);

	}


	/**
	 * Get whether a particular item claim has questions 
	 * associated with it in a given questions data object.
	 *
	 * @param {Object} questionsData E.g. `/api/checkout/basket/{Basket}/questions`
	 * @param {Object} itemClaim `CheckoutBasketItem` object
	 * @return {Boolean}
	 */
	static itemHasQuestions(questionsData, itemClaim) {
		return (questionsData?.Items?.hasOwnProperty(itemClaim.Uuid) || questionsData?.People?.hasOwnProperty(itemClaim.Uuid));
	}


	/**
	 * Validate an answer's value against its data-type.
	 *
	 * Note: For the multiple choice data type, does not validate 
	 * that the specified option ID is actually valid for the question.
	 * 
	 * @param {mixed} value
	 * @param {Integer} dataType Order question data type case index
	 * @return {Boolean}
	 */
	static validateAnswer(value, dataType) {
		if ((dataType === DataTypes.TrueFalse) || (dataType === DataTypes.YesNo)) {
			return ([true, false, 1, 0].includes(value));
		}
		else if (dataType === DataTypes.MultipleChoice) {
			return Number.isInteger(value);
		}
		else if (dataType === DataTypes.Number) {
			const val = parseInt(value);
			return (Number.isInteger(val) && (val >= 0));
		}
		else if (dataType === DataTypes.Text) {
			return ((typeof value === "string") && (value.length > 0));
		}
		else if (dataType === DataTypes.Str) {
			return ((typeof value === "string") && (value.length > 0) && (value.length <= 250));
		}
		else return false;
	}

}

export default OrderQuestionService;
